import { useEffect } from 'react'
import type { RefObject } from 'react'

type UseAccessibleTabsParams = {
  containerRef: RefObject<HTMLElement>
  activeIndex: number
}

function getFocusTargetIndex(
  key: string,
  currentIndex: number,
  total: number
): number | null {
  switch (key) {
    case 'ArrowRight':
      return (currentIndex + 1) % total
    case 'ArrowLeft':
      return (currentIndex - 1 + total) % total
    case 'Home':
      return 0
    case 'End':
      return total - 1
    default:
      return null
  }
}

export function useAccessibleTabs({
  containerRef,
  activeIndex
}: UseAccessibleTabsParams): void {
  useEffect(() => {
    const tabList = containerRef.current?.querySelector('ul')

    if (!tabList) {
      return
    }

    const tabItems = Array.from(tabList.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement
    )

    const tabs = tabItems.map(
      tabItem => tabItem.querySelector('button') ?? tabItem
    )

    if (!tabs.length) {
      return
    }

    tabList.setAttribute('role', 'tablist')

    tabItems.forEach((tabItem, index) => {
      if (tabItem !== tabs[index]) {
        tabItem.setAttribute('role', 'presentation')
      }
    })

    tabs.forEach((tab, index) => {
      const isSelected = index === activeIndex

      tab.setAttribute('role', 'tab')
      tab.setAttribute('aria-selected', String(isSelected))
      tab.setAttribute('tabindex', '0')
      tab.removeAttribute('aria-current')
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      const currentIndex = tabs.indexOf(event.target as HTMLElement)

      if (currentIndex === -1) {
        return
      }

      const focusTargetIndex = getFocusTargetIndex(
        event.key,
        currentIndex,
        tabs.length
      )

      if (focusTargetIndex !== null) {
        event.preventDefault()
        tabs[focusTargetIndex].focus()
        return
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        tabs[currentIndex].click()
      }
    }

    tabList.addEventListener('keydown', handleKeyDown)

    return () => {
      tabList.removeEventListener('keydown', handleKeyDown)
    }
  })
}
